import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link,
  VStack,
} from '@chakra-ui/react';
import { RegisterData, useUserContext } from '@providers/useUserContext';
import FloatingLabelInput from 'components/FloatingLabelInput';
import { useReferralContext } from 'hooks/useReferralContext';
import { FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientException } from 'utils/apiClientException';
import { ReferalTypeSelect } from '../ReferalTypeSelect';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import('react-phone-number-input/style.css');

export function Register() {
  const { oauth } = mondayMindbody ?? {};
  const { user } = oauth;
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const referralContext = useReferralContext();
  // L'Id Mindbody d'un client n'est pas toujours numérique : les anciens
  // profils migrés ont un Id comme `MSC444660`. Il part tel quel au serveur ;
  // un `Number()` le transformait en NaN et le parrain était perdu sans erreur
  // (cas Magali Marti, Riise Paris, 30/09/2026).
  const ReferralClientId = referralContext.referrerClientId || null;
  const ReferralCampaignId = referralContext.campaignId;

  console.log('[OAuth Register] Registration form loaded', {
    oauthUserEmail: user?.email,
    oauthUserName: `${user?.given_name} ${user?.family_name}`,
    hasReferralId: !!ReferralClientId,
    referralId: ReferralClientId,
    referralCampaignId: ReferralCampaignId,
    referralMode: referralContext.mode,
  });

  Sentry.addBreadcrumb({
    category: 'oauth',
    message: 'OAuth registration form loaded',
    level: 'info',
    data: {
      oauthUserEmail: user?.email,
      hasReferralId: !!ReferralClientId,
    },
  });

  Sentry.captureMessage('OAuth: Registration form displayed (user should not exist)', {
    level: 'warning',
    tags: {
      flow: 'oauth',
      step: 'register_form_shown',
    },
    extra: {
      oauthUserEmail: user?.email,
      oauthUserName: `${user?.given_name} ${user?.family_name}`,
      hasReferralId: !!ReferralClientId,
    },
  });

  const methods = useForm<RegisterData>({
    defaultValues: {
      Email: user?.email ?? '',
      FirstName: user?.given_name ?? '',
      LastName: user?.family_name ?? '',
      ScheduleEmails: true,
      PromotionalEmails: true,
      ReferredBy: ReferralClientId ? 'Another Client' : undefined,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const { signUp } = useUserContext();

  async function onSubmit(data: RegisterData) {
    console.log('[OAuth Register] Submitting registration form', {
      email: data.Email,
      firstName: data.FirstName,
      lastName: data.LastName,
      hasReferralId: !!data.ReferralClientId,
      hasBirthDate: !!data.BirthDate,
      hasPhone: !!data.MobilePhone,
    });

    Sentry.addBreadcrumb({
      category: 'oauth',
      message: 'Submitting registration form',
      level: 'info',
      data: {
        email: data.Email,
        hasReferralId: !!data.ReferralClientId,
      },
    });

    try {
      if (ReferralClientId) data.ReferralClientId = ReferralClientId;
      if (ReferralCampaignId) data.ReferralCampaignId = ReferralCampaignId;
      await signUp(data);

      console.log('[OAuth Register] Registration completed successfully, navigating...');

      // Breadcrumb : succès nominal, et sans l'e-mail (PII).
      sentryTrace('OAuth: Registration completed successfully', {
        tags: {
          flow: 'oauth',
          step: 'register_success',
        },
      });

      if (locationState && locationState.onLoginRedirect) {
        navigate(locationState.onLoginRedirect, { state: null });
      } else {
        navigate('.');
      }
    } catch (error: any) {
      console.error('[OAuth Register] Registration failed', {
        error: error?.message || error,
        errorType: error?.constructor?.name,
      });

      Sentry.captureException(error, {
        tags: {
          flow: 'oauth',
          step: 'register_error',
        },
        extra: {
          email: data.Email,
          errorMessage: error?.message || String(error),
        },
      });

      if (error instanceof ApiClientException) {
        const message = error.getMessage();
        console.error('[OAuth Register] ApiClientException:', message);
        setError('root', { message });
      }
    }
  }

  if (!user) return null;

  return (
    <FormProvider {...methods}>
      <VStack className="signUpForm" align="stretch" as="form" onSubmit={handleSubmit(onSubmit)} maxW="960px" mx="auto">
        <Heading as="h1" textAlign="center">
          Bienvenue {mondayMindbody.oauth.user?.given_name}, nous avons besoin de quelques informations pour compléter
          ton inscription.
        </Heading>

        <FloatingLabelInput
          name="BirthDate"
          label="Date de naissance*"
          type="date"
          registerOptions={{ required: true }}
        />

        <FormControl>
          <Box maxW="400px">
            <FormLabel textTransform={'uppercase'}>Téléphone*</FormLabel>
            <PhoneInput
              international
              required
              inputComponent={Input}
              defaultCountry="FR"
              numberInputProps={register('MobilePhone')}
              onChange={(value) => {
                if (value) setValue('MobilePhone', value);
              }}
            />
          </Box>
        </FormControl>

        <ReferalTypeSelect />

        <VStack>
          <Box maxW="400px" width="100%">
            <FormControl textAlign={'left'}>
              <Checkbox borderColor="black" {...register('PromotionalEmails')} textAlign={'left'}>
                Recevoir les actualités, événements et offres
              </Checkbox>
            </FormControl>
          </Box>

          <Box maxW="400px" width="100%">
            <FormControl isInvalid={!!errors.TermsAndConditions} textAlign={'left'}>
              <Checkbox borderColor="black" {...register('TermsAndConditions', { required: true })} textAlign={'left'}>
                Je comprends et j’accepte les{' '}
                <Link textDecoration="underline" href="#">
                  conditions générales de vente*
                </Link>
              </Checkbox>
            </FormControl>
          </Box>
        </VStack>
        <FormControl isInvalid={!!errors.root} alignSelf="center" justifyContent={'center'} mt={1}>
          <FormErrorMessage textAlign="center" justifyContent={'center'}>
            {errors.root?.message}
          </FormErrorMessage>
        </FormControl>
        <VStack padding="2em 1em">
          <Button textAlign="center" margin="auto" type="submit" variant="contained" isLoading={isSubmitting}>
            Compléter mon inscription
          </Button>
        </VStack>
      </VStack>
    </FormProvider>
  );
}
